import type { ReactNode } from "react";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import type { RootState } from "../../redux/store";
import api from "../../services/axios";
import { normalizePermissionKeys } from "../../utils/permissionKeyNormalization";
import { hasPermissionByRole, hasPermissionCode } from "../../utils/permissionUtils";
import { setPermissions } from "../permissionsSlice";

import { PermissionContext } from "./PermissionContext";

type PermissionVersionResponse = {
  userId: string;
  tenantId: string;
  permissionVersion: number;
};

type PermissionsResponse = {
  userId: string;
  tenantId: string;
  permissionVersion: number;
  effectivePermissionKeys: string[];
};

const AUTH_SESSION_STORAGE_KEY = "auth_session_v1";
// Cost/latency trade-off:
// - This endpoint is lightweight and cached server-side.
// - Poll only while the tab is visible.
// - 60s gives near-real-time updates without excessive traffic.
const PERMISSION_POLL_MS = 60_000;
const PERMISSION_POLL_JITTER_MS = 15_000;

export function PermissionProvider({ children }: { children: ReactNode }) {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const userPermissions = useSelector((state: RootState) => state.userPermissions);
  const permissionCodes = useMemo(
    () => userPermissions.permissionCodes || [],
    [userPermissions.permissionCodes],
  );

  useEffect(() => {
    if (!user && permissionCodes.length > 0) {
      dispatch(setPermissions([]));
    }
  }, [dispatch, permissionCodes.length, user]);

  useEffect(() => {
    if (!user?.id) return;
    if (!user?.tenantId) return;

    let disposed = false;
    let inFlight = false;
    let lastHash = permissionCodes.join("\n");
    let lastPermissionVersion: number | null = null;

    const tryReadStoredPermissionVersion = () => {
      try {
        const raw = localStorage.getItem(AUTH_SESSION_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Record<string, unknown>;
        const v = parsed?.permissionVersion;
        return typeof v === "number" ? v : null;
      } catch {
        return null;
      }
    };

    lastPermissionVersion = tryReadStoredPermissionVersion();

    const unwrap = <T,>(input: unknown): T | null => {
      if (!input || typeof input !== "object") return null;
      const obj = input as Record<string, unknown>;
      // Support common API wrappers: { data: {...} } or { success, data: {...} }
      const candidate = (obj.data as unknown) ?? obj;
      return (candidate ?? null) as T | null;
    };

    const sync = async () => {
      if (disposed) return;
      if (inFlight) return;
      if (document.visibilityState !== "visible") return;

      inFlight = true;
      try {
        const versionResp = await api.get<PermissionVersionResponse>(
          "/auth/permission-version",
          {
            headers: {
              // defensive: some middlewares expect tenant header even for /me
              ...(user.tenantId ? { "X-Tenant-Id": user.tenantId } : {}),
            },
          },
        );

        const versionPayload = unwrap<PermissionVersionResponse>(versionResp.data);
        const remoteVersion =
          typeof versionPayload?.permissionVersion === "number"
            ? versionPayload.permissionVersion
            : null;

        const storedVersion = lastPermissionVersion ?? tryReadStoredPermissionVersion();
        const shouldRefresh =
          remoteVersion !== null &&
          (storedVersion === null || remoteVersion !== storedVersion);

        if (!shouldRefresh) {
          return;
        }

        const permsResp = await api.get<PermissionsResponse>("/auth/permissions", {
          headers: {
            ...(user.tenantId ? { "X-Tenant-Id": user.tenantId } : {}),
          },
        });

        const permsPayload = unwrap<PermissionsResponse>(permsResp.data);

        const keys = Array.isArray(permsPayload?.effectivePermissionKeys)
          ? permsPayload.effectivePermissionKeys
          : [];

        const normalizedKeys = normalizePermissionKeys(keys);

        const nextHash = normalizedKeys.join("\n");
        if (!disposed && nextHash !== lastHash) {
          lastHash = nextHash;
          dispatch(setPermissions(normalizedKeys));
        }

        const nextVersion =
          typeof permsPayload?.permissionVersion === "number"
            ? permsPayload.permissionVersion
            : null;

        if (nextVersion !== null) {
          lastPermissionVersion = nextVersion;
        }

        // Keep persisted auth session in sync so reload picks up latest permissions.
        try {
          const raw = localStorage.getItem(AUTH_SESSION_STORAGE_KEY);
          if (raw) {
            const parsed = JSON.parse(raw);
            const updated = {
              ...parsed,
              effectivePermissionKeys: normalizedKeys,
              ...(nextVersion !== null ? { permissionVersion: nextVersion } : {}),
            };
            localStorage.setItem(AUTH_SESSION_STORAGE_KEY, JSON.stringify(updated));
          }
        } catch {
          // ignore storage issues
        }
      } catch {
        // ignore background refresh errors (token expiry handled elsewhere)
      } finally {
        inFlight = false;
      }
    };

    // Fast refresh on focus/visibility, plus periodic refresh.
    const onFocus = () => void sync();
    const onVisibility = () => void sync();

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    void sync();
    // Add a small randomized delay so many clients don't poll at the exact same time.
    const jitter = Math.floor(Math.random() * PERMISSION_POLL_JITTER_MS);
    let interval: number | null = null;
    const timeout = window.setTimeout(() => {
      if (disposed) return;
      interval = window.setInterval(sync, PERMISSION_POLL_MS);
    }, jitter);

    return () => {
      disposed = true;
      window.clearTimeout(timeout);
      if (interval !== null) {
        window.clearInterval(interval);
      }
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [dispatch, user?.id, user?.tenantId, permissionCodes]);

  const can = useMemo(() => {
    return (permission?: string): boolean => {
      if (!permission) return true;
      if (!user) return false;

      // PlatformOwner is authorized for everything by role identity alone, mirroring
      // the backend's AuthorizationPolicies.PlatformOwner (claim check, no permission
      // keys) — its effectivePermissionKeys are intentionally empty.
      if (user.platformRoles?.includes("PlatformOwner")) return true;

      // Staff path: existing permission-key logic remains unchanged.
      if (permissionCodes.length > 0) {
        return hasPermissionCode(permission, permissionCodes);
      }

      const parts = permission.split(".");
      if (parts.length === 3) {
        const [module, feature, action] = parts;
        return hasPermissionByRole(module, feature, action, user.role);
      }

      if (parts.length === 2) {
        const [module, feature] = parts;
        return hasPermissionByRole(module, feature, "view", user.role);
      }

      return false;
    };
  }, [permissionCodes, user]);

  return (
    <PermissionContext.Provider value={{ can, permissionCodes }}>
      {children}
    </PermissionContext.Provider>
  );
}
